/**
 * Tracks whether the app is currently applying server-originated session state — a broadcast
 * or a reconnect read-repair GET (AVS-4330). The outbound sync layer reads this to tell a
 * remote-applied change apart from a genuine local edit.
 *
 * Without it, applying a broadcast fires `change` synchronously, `syncToServer` marks the
 * applied keys dirty, and the NEXT broadcast for those keys is skipped by the dirty-key gate —
 * so every shared key stops syncing after its first update on the receiving side.
 *
 * A customer's 3D Showroom announcement also runs under it (`runShowroomAnnouncement`, AVS-4412),
 * so a customer's `skip:applying-remote` decision is not always a server payload.
 *
 * Dependency-free (like `remoteVersion.ts`) so both `applyRemote` and `syncToServer` can read it
 * without an import cycle.
 */

let applyingRemote = false;

export function isApplyingRemote(): boolean {
  return applyingRemote;
}

/**
 * Run `fn` with the remote-apply flag set, restoring the previous value even if `fn` throws.
 * Restores the PREVIOUS value (not `false`) so a nested call keeps the flag set until the
 * outermost `runApplyingRemote` returns.
 */
export function runApplyingRemote<T>(fn: () => T): T {
  const previous = applyingRemote;
  applyingRemote = true;
  try {
    return fn();
  } finally {
    applyingRemote = previous;
  }
}
